import { Card, Descriptions, Space, Tag, Typography, Table, Divider, Button } from "antd";
import { useShow } from "@refinedev/core";
import { useEffect, useState } from "react";
import { productService } from "../../../api/productService";
import type { IStockMovementSummary } from "../../../interfaces/product";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

export const StockMovementSummary: React.FC<{ productId: number }> = ({ productId }) => {
    const [stockMovement, setStockMovement] = useState<IStockMovementSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStockMovement = async () => {
            try {
                const data = await productService.getStockMovementSummary(productId);
                setStockMovement(data);
            } catch (error) {
                console.error("Failed to fetch stock movement summary:", error);
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            fetchStockMovement();
        }
    }, [productId]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString: string | undefined | null) => {
        if (!dateString) return "-";
        return dayjs(dateString).format("YYYY-MM-DD HH:mm");
    };

    const recentPurchasesColumns = [
        {
            title: 'PO Number',
            dataIndex: 'purchaseOrderNumber',
            key: 'purchaseOrderNumber',
            render: (text: string, record: any) => (
                <Button 
                    type="link" 
                    onClick={() => navigate(`/purchases/show/${record.purchaseOrderId}`)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'Vendor',
            dataIndex: 'vendorName',
            key: 'vendorName',
            render: (text: string) => text || '-',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right' as const,
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            align: 'right' as const,
            render: (price: number) => formatCurrency(price),
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            align: 'right' as const,
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Date',
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            render: (date: string) => formatDate(date),
        },
    ];

    const recentSalesColumns = [
        {
            title: 'Invoice Number',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (text: string, record: any) => (
                <Button 
                    type="link" 
                    onClick={() => navigate(`/sales/show/${record.saleId}`)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
            render: (text: string) => text || '-',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right' as const,
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            align: 'right' as const,
            render: (price: number) => formatCurrency(price),
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            align: 'right' as const,
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Date',
            dataIndex: 'saleDate',
            key: 'saleDate',
            render: (date: string) => formatDate(date),
        },
    ];

    if (loading) {
        return <Card title="Stock Movement Summary" loading={true} />;
    }

    if (!stockMovement) {
        return (
            <Card title="Stock Movement Summary">
                <Text>No stock movement data available.</Text>
            </Card>
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card title="Stock Movement Summary">
                <Descriptions bordered column={2}>
                    <Descriptions.Item label="Current Stock Level" span={1}>
                        <Text strong style={{ color: stockMovement.currentStockLevel > 0 ? '#52c41a' : '#ff4d4f' }}>
                            {stockMovement.currentStockLevel.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Available Stock" span={1}>
                        <Text strong style={{ color: stockMovement.availableStock > 0 ? '#52c41a' : '#ff4d4f' }}>
                            {stockMovement.availableStock.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Reserved Stock" span={1}>
                        {stockMovement.reservedStock.toFixed(2)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Stock Status" span={1}>
                        <Tag color={stockMovement.availableStock > 0 ? 'green' : 'red'}>
                            {stockMovement.availableStock > 0 ? 'Available' : 'Out of Stock'}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Sales Summary">
                <Descriptions bordered column={2}>
                    <Descriptions.Item label="Total Sales Quantity">
                        <Text strong>{stockMovement.totalSalesQuantity.toFixed(2)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Sales Amount">
                        <Text strong>{formatCurrency(stockMovement.totalSalesAmount)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Number of Sales">
                        {stockMovement.salesCount}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Sale Date">
                        {formatDate(stockMovement.lastSaleDate)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Sale Invoice" span={2}>
                        {stockMovement.lastSaleInvoiceNumber && stockMovement.recentSales.length > 0 ? (
                            <Button 
                                type="link" 
                                onClick={() => navigate(`/sales/show/${stockMovement.recentSales[0].saleId}`)}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {stockMovement.lastSaleInvoiceNumber}
                            </Button>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Purchase Summary">
                <Descriptions bordered column={2}>
                    <Descriptions.Item label="Total Purchase Quantity">
                        <Text strong>{stockMovement.totalPurchaseQuantity.toFixed(2)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Purchase Amount">
                        <Text strong>{formatCurrency(stockMovement.totalPurchaseAmount)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Number of Purchases">
                        {stockMovement.purchaseCount}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Purchase Date">
                        {formatDate(stockMovement.lastPurchaseDate)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Purchase Order" span={2}>
                        {stockMovement.lastPurchaseOrderNumber && stockMovement.recentPurchases.length > 0 ? (
                            <Button 
                                type="link" 
                                onClick={() => navigate(`/purchases/show/${stockMovement.recentPurchases[0].purchaseOrderId}`)}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {stockMovement.lastPurchaseOrderNumber}
                            </Button>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {stockMovement.recentPurchases.length > 0 && (
                <Card title="Recent Purchases (Last 5)">
                    <Table
                        columns={recentPurchasesColumns}
                        dataSource={stockMovement.recentPurchases}
                        rowKey="purchaseOrderId"
                        pagination={false}
                        size="small"
                    />
                </Card>
            )}

            {stockMovement.recentSales.length > 0 && (
                <Card title="Recent Sales (Last 5)">
                    <Table
                        columns={recentSalesColumns}
                        dataSource={stockMovement.recentSales}
                        rowKey="saleId"
                        pagination={false}
                        size="small"
                    />
                </Card>
            )}
        </Space>
    );
};